import { Button, Checkbox, Divider, Drawer, Form, Select, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../assets/css/search-filters-drawer.css';
import {
  useGetAllFacilitiesQuery,
  useGetAllQuickFactsQuery,
} from '../../fishery_profile/fisherySlice';

function DrawerSection({ title, description, children }) {
  return (
    <section className='search-drawer-section gMedium grey-900'>
      <h3 className='gSemiBold fs-16 lh-1.25'>{title}</h3>
      <p className='fs-12 mg-bottom-20'>{description}</p>
      {children}
      <Divider className='grey-border-100' />
    </section>
  );
}

function SpeciesWeightSelector({ form, name }) {
  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <>
          {fields.map((field, index) => {
            const selectedSpecies = form.getFieldValue([
              name,
              field.name,
              'species',
            ]);
            return (
              <Form.Item className='species-filter__row'>
                <div className='species-filter grey-bg-100 br10 pd-15'>
                  <Form.Item
                    {...field}
                    name={[field.name, 'species']}
                    label={'Species'}
                  >
                    <Select options={species} />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'weight']}
                    label={'Weight'}
                  >
                    <Select
                      disabled={!selectedSpecies}
                      options={species
                        .find(({ value }) => value === selectedSpecies)
                        ?.weights.map((w) => ({
                          value: w,
                          label: `Over ${w}lbs`,
                        }))}
                    />
                  </Form.Item>
                </div>
                <Button
                  className='species-filter__remove grey-400 fs-22 gLight'
                  type={'text'}
                  onClick={() => {
                    remove(field.name);
                  }}
                >
                  x
                </Button>
              </Form.Item>
            );
          })}
          <Button
            className='species-filter__add justify-center sb-blue-600 fs-16 txt-center mg-auto-lr gBold'
            type={'text'}
            onClick={() => {
              add();
            }}
          >
            Add {!fields?.length ? 'Filter' : 'More'} +
          </Button>
        </>
      )}
    </Form.List>
  );
}

function ToggleButtonWrapper({
  value = false,
  onChange,
  activeClassName = 'active',
  children,
}) {
  const [localValue, setLocalValue] = useState(value);
  useEffect(() => setLocalValue(value), [value]);

  const newChildren = React.Children.map(children, (child) =>
    React.cloneElement(child, {
      className:
        `${child?.props?.className ?? ''} ` +
        (localValue ? activeClassName : ''),
    })
  );

  const handleClick = () => {
    setLocalValue((prev) => {
      onChange(!prev);
      return !prev;
    });
  };

  return (
    <div className='cp' onClick={handleClick}>
      {newChildren}
    </div>
  );
}

const carpWeights = [20, 30, 40, 50, 60];
const coarseWeights = [1, 2, 3];

const species = [
  {
    value: 'Carp',
    label: 'Carp (Any)',
    weights: carpWeights,
  },
  { label: 'Mirror Carp', weights: carpWeights },
  { label: 'Common Carp', weights: carpWeights },
  { label: 'Crucian Carp', weights: carpWeights },
  { label: 'Grass Carp', weights: carpWeights },
  { label: 'Leather Carp', weights: carpWeights },
  { label: 'Koi Carp', weights: carpWeights },
  {
    value: 'F1',
    label: 'F1 Carp',
    weights: coarseWeights,
  },
  { label: 'Pike', weights: [10, 20, 30] },
  { label: 'Catfish', weights: [30, 50, 80, 100] },
  { label: 'Tench', weights: [3, 5, 7] },
  { label: 'Perch', weights: [1, 2, 3, 4] },
  { label: 'Barbel', weights: [2, 4, 6, 8, 10] },
  { label: 'Sturgeon', weights: [20, 40, 60, 80] },
  { label: 'Bream', weights: [5, 10, 15] },
  { label: 'Trout', weights: [2, 4, 7, 10] },
  { label: 'Roach', weights: coarseWeights },
  { label: 'Rudd', weights: coarseWeights },
  { label: 'Dace', weights: coarseWeights },
  { label: 'Grayling', weights: coarseWeights },
  { label: 'Goldfish', weights: coarseWeights },
  { label: 'Silverfish', weights: coarseWeights },
  { label: 'Orph', weights: coarseWeights },
  { label: 'Chub', weights: coarseWeights },
].map((s) => ({ ...s, value: s?.value ?? s.label }));

const selectableSpecies = species.map(({ value }) => value);

const SPECIES_PATTERN = /(.*):([0-9]+)\+/;

export default function SearchFiltersDrawer({
  open,
  onClose,
  initialFilters,
  setSearchFilters,
}) {
  const [form] = Form.useForm();
  const [changed, setChanged] = useState(false);
  const [speciesRenderKey, setSpeciesRenderKey] = useState(new Date());

  const { data: quickFacts, isLoading: loadingQuickFacts } =
    useGetAllQuickFactsQuery();
  const quickFactsNames = useMemo(
    () => (quickFacts ?? []).map(({ name }) => name),
    [quickFacts]
  );
  const { data: facilities, isLoading: loadingFacilities } =
    useGetAllFacilitiesQuery();
  const facilitiesNames = useMemo(
    () => (facilities ?? []).map(({ name }) => name),
    [facilities]
  );

  const bookingTypes = useMemo(
    () =>
      [
        {
          key: 'ticket',
          label: 'Watercraft Ticket',
          filter: 'lake_booking_types',
          value: 'ticket',
        },
        {
          key: 'swim',
          label: 'Specific Swims',
          filter: 'lake_booking_types',
          value: 'swim',
        },
        {
          key: 'exclusive',
          label: 'Lake Exclusive',
          filter: 'lake_booking_types',
          value: 'lake-exclusive',
        },
        {
          key: 'members',
          label: 'Membership',
          filter: 'lake_booking_types',
          value: 'member-only',
        },
        {
          key: 'night',
          label: 'Night Fishing',
          filter: 'quick_facts',
          value: (quickFacts ?? []).find(({ name }) => name === 'Night Fishing')
            ?.name,
        },
        {
          key: 'custom-slots',
          label: 'Fixed Packages',
          filter: 'lake_booking_types',
          value: 'custom-slots',
        },
      ].filter(({ value }) => value),
    [quickFacts]
  );

  useEffect(() => {
    if (!open) {
      setChanged(false);
    }
  }, [open]);

  const getInitialFormState = useCallback(() => {
    return {
      booking: Object.fromEntries(
        (bookingTypes ?? []).map(({ key, filter, value }) => [
          key,
          initialFilters[filter].includes(value),
        ])
      ),
      facilities: Object.fromEntries(
        facilitiesNames.map((name) => [
          name,
          initialFilters.facilities.includes(name),
        ])
      ),
      facts: Object.fromEntries(
        quickFactsNames.map((name) => [
          name,
          initialFilters.quick_facts.includes(name),
        ])
      ),
      species: initialFilters.species
        .map((sTxt) => {
          const match = sTxt.match(SPECIES_PATTERN);
          if (match) {
            return {
              species: match?.[1],
              weight: match?.[2] ? parseInt(match[2]) : null,
            };
          }
          return null;
        })
        .filter((v) => v),
    };
  }, [bookingTypes, facilitiesNames, initialFilters, quickFactsNames]);

  useEffect(() => {
    if (!open) {
      return;
    }
    form.setFieldsValue(getInitialFormState());
  }, [form, getInitialFormState, open]);

  const handleFiltersChange = (data) => {
    if (changed) {
      const getBookingTypeOptionsForFilter = (filter) =>
        bookingTypes
          .filter(({ filter: f }) => f === filter)
          .map((bt) => [bt.value, Boolean(data.booking[bt.key])]);

      const newFilterValues = {
        lake_booking_types: Object.fromEntries([
          ...getBookingTypeOptionsForFilter('lake_booking_types'),
        ]),
        facilities: Object.fromEntries(
          [
            ...Object.entries(data.facilities),
            ...getBookingTypeOptionsForFilter('facilities'),
          ].map(([k, v]) => [k, Boolean(v)])
        ),
        quick_facts: Object.fromEntries(
          [
            ...Object.entries(data.facts),
            ...getBookingTypeOptionsForFilter('quick_facts'),
          ].map(([k, v]) => [k, Boolean(v)])
        ),
        species: (data.species ?? []).map(
          ({ species: sName }) =>
            `${sName}:${
              data.species.find(({ species }) => species === sName)?.weight
            }+`
        ),
      };

      const getNewFiltersValue = (prevValues, newValues) =>
        // Returns prevValues with the values updated (keeping any values which are not options in this drawer)
        prevValues
          // Remove any previous values which are options in this drawer (contained within newValues) but are false (unchecked)
          .filter((v) => newValues?.[v] !== false)
          // Add any checked options
          .concat(
            Object.entries(newValues)
              .filter(([, v]) => v)
              .map(([k]) => k)
          );

      setSearchFilters((prev) => ({
        ...prev,
        lake_booking_types: getNewFiltersValue(
          prev.lake_booking_types,
          newFilterValues.lake_booking_types
        ),
        facilities: getNewFiltersValue(
          prev.facilities,
          newFilterValues.facilities
        ),
        quick_facts: getNewFiltersValue(
          prev.quick_facts,
          newFilterValues.quick_facts
        ),
        species: [
          ...prev.species.filter(
            (s) =>
              !s?.match?.(SPECIES_PATTERN)?.[2] ||
              !selectableSpecies.includes(s.match(SPECIES_PATTERN)[1])
          ),
          ...newFilterValues.species,
        ],
      }));
      setChanged(false);
    }

    onClose();
  };

  const drawerBody = (
    <>
      <h2 className='sb-blue-900 gBold fs-20 txt-center w-full'>
        Refine Your Search
      </h2>

      <Form
        form={form}
        className=''
        layout='horizontal'
        colon={false}
        onValuesChange={() => {
          setChanged(true);
          setSpeciesRenderKey(new Date());
        }}
        onFinish={handleFiltersChange}
      >
        <DrawerSection
          title='Booking Type'
          description='Choose the specific type of lake you are looking for'
        >
          <div className='search-drawer-buttons'>
            {bookingTypes.map((field) => (
              <Form.Item name={['booking', field.key]}>
                <ToggleButtonWrapper>
                  <Button className='brFull grey-bg-100 gBold fs-12 grey-900 grid-center'>
                    <span className=' txt-single-line-ellipsis'>
                      {field.label}
                    </span>
                  </Button>
                </ToggleButtonWrapper>
              </Form.Item>
            ))}
          </div>
        </DrawerSection>

        <DrawerSection
          title='Facts & Facilities'
          description='Choose lakes that have your preferred facilities'
        >
          <div className='search-drawer-checkboxes'>
            {loadingQuickFacts || loadingFacilities ? (
              <Spin />
            ) : (
              <>
                {(facilities ?? []).map((field) => (
                  <Form.Item
                    name={['facilities', field.name]}
                    label={field.name}
                    valuePropName='checked'
                  >
                    <Checkbox className='xl-checkbox' />
                  </Form.Item>
                ))}
                {(quickFacts ?? [])
                  .filter(
                    ({ name }) =>
                      ![
                        'Members Only',
                        'Night Fishing',
                        'Lake exclusive',
                      ].includes(name)
                  )
                  .map((field) => (
                    <Form.Item
                      name={['facts', field.name]}
                      label={field.name}
                      valuePropName='checked'
                    >
                      <Checkbox className='xl-checkbox' />
                    </Form.Item>
                  ))}
              </>
            )}
          </div>
        </DrawerSection>

        <DrawerSection
          title='Fish Species & Size'
          description='Choose lakes that have your target species and fish size'
        >
          <SpeciesWeightSelector
            key={
              speciesRenderKey.toString() /* Used to trigger a refresh of disabled state/selectable options when species is changed */
            }
            form={form}
            name='species'
          />
        </DrawerSection>
      </Form>
    </>
  );

  const drawerFooter = (
    <div className='justify-between row-center'>
      <Button
        type='text'
        className='fs-16 gSemiBold sb-blue-900'
        onClick={() => {
          form.resetFields();
          setChanged(true);
        }}
      >
        Clear all
      </Button>
      <Button
        type='primary'
        className='br5 sb-blue-bg-500 hover:sb-blue-bg-700 border-1 sb-blue-border-300 hover:sb-blue-border-100 fs-16 gSemiBold h-fit pd-top-10 pd-bottom-10'
        onClick={() => form.submit()}
      >
        Save Changes
      </Button>
    </div>
  );

  return (
    <>
      <Drawer
        placement={'bottom'}
        open={open}
        closeable={false}
        maskCloseable={true}
        title={null}
        header={null}
        footer={drawerFooter}
        className={
          'search-drawer search-filter-drawer search-filter-drawer--mobile'
        }
        height={'100%'}
        width={'100%'}
        zIndex={1005}
        onClose={onClose}
      >
        {drawerBody}
      </Drawer>
      <Drawer
        placement={'right'}
        open={open}
        closeable={false}
        maskCloseable={true}
        title={null}
        header={null}
        footer={drawerFooter}
        className={
          'search-drawer search-filter-drawer search-filter-drawer--desktop'
        }
        height={'100%'}
        width={'100%'}
        zIndex={1005}
        onClose={onClose}
      >
        {drawerBody}
      </Drawer>
    </>
  );
}
